/** Tiny DOM helpers. Everything goes through textContent — no HTML interpolation. */

type Child = Node | string | null | undefined | false;

interface ElOpts {
  class?: string;
  text?: string;
  type?: string;
  attrs?: Record<string, string>;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: ElOpts = {},
  children: Child[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (opts.class) node.className = opts.class;
  if (opts.text !== undefined) node.textContent = opts.text;
  if (opts.type && node instanceof HTMLButtonElement) node.type = opts.type as "button" | "submit";
  for (const [k, v] of Object.entries(opts.attrs ?? {})) node.setAttribute(k, v);
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    node.append(c);
  }
  return node;
}

export function button(
  label: string,
  cls: string,
  onClick: (ev: MouseEvent) => void,
): HTMLButtonElement {
  const b = el("button", { class: cls, text: label, type: "button" });
  b.addEventListener("click", onClick);
  return b;
}

export function clear(node: Element): void {
  node.replaceChildren();
}

export function byId<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing element #${id}`);
  return node as T;
}

/** Hungarian one-decimal average: 4.25 → "4,3". */
export function fmtScore(n: number | null): string {
  if (n === null) return "–";
  return n.toFixed(1).replace(".", ",");
}

const dateFmt = new Intl.DateTimeFormat("hu-HU", { year: "numeric", month: "long", day: "numeric" });

export function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : dateFmt.format(d);
}

/** "3 értékelés" / "nincs még értékelés" */
export function fmtCount(n: number): string {
  return n === 0 ? "nincs még értékelés" : `${n} értékelés`;
}

/** Debounce trailing-edge: the last call within `ms` wins. */
export function debounce<A extends unknown[]>(ms: number, fn: (...args: A) => void): (...args: A) => void {
  let timer: number | undefined;
  return (...args: A) => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), ms);
  };
}
